'use client';

import dynamic from 'next/dynamic';
import type { BookingBlock as BookingBlockType, PageSettings } from '@shared/types/salespads';
import { DEFAULT_BOOKING_MODAL_BUTTON } from '@shared/types/salespads';
import { isSmartFlow } from '@shared/booking/form-variant';
import type { EventData } from '@/lib/fetch-event-data';
import { BookingModalTrigger } from './BookingModalTrigger';
import { BookingFormSkeleton } from '../booking/BookingFormSkeleton';

// Lazy-load the heavy BookingWidget (date-fns, Supabase, phone-input, etc.) so
// pages without booking blocks don't download any of this JS. Two wrappers over
// the same chunk so the loading skeleton matches the event's form variant — the
// next/dynamic `loading` callback can't see props, so we pick the wrapper from
// the parent (which knows form_style).
const widgetImport = () => import('../booking/BookingWidget').then(m => ({ default: m.BookingWidget }));
const BookingWidgetSmartFlow = dynamic(widgetImport, { ssr: false, loading: () => <BookingFormSkeleton variant="smartflow" /> });
const BookingWidgetClassic = dynamic(widgetImport, { ssr: false, loading: () => <BookingFormSkeleton variant="classic" /> });

interface Props {
  block: BookingBlockType;
  settings: PageSettings;
  eventData?: EventData;
}

export function BookingBlock({ block, settings: _settings, eventData }: Props) {
  const { data } = block;

  if (!data.eventId || !eventData) {
    return (
      <div className="w-full">
        <div className="p-12 text-center rounded-lg border border-gray-200 bg-white">
          <svg
            width={48}
            height={48}
            className="w-12 h-12 mx-auto mb-4 text-gray-300"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
          <p className="text-gray-400 text-sm">
            {data.eventId ? 'Event not found or not published' : 'No event selected'}
          </p>
        </div>
      </div>
    );
  }

  // Modal display mode (SmartFlow only) — render a button that opens the popup.
  // Falls back to inline if the selected event isn't SmartFlow.
  if (data.displayMode === 'modal' && isSmartFlow(eventData.event.form_style)) {
    return (
      <div className="w-full">
        <BookingModalTrigger
          button={data.button ?? DEFAULT_BOOKING_MODAL_BUTTON}
          eventData={eventData}
          blockId={block.id}
        />
      </div>
    );
  }

  const Widget = isSmartFlow(eventData.event.form_style) ? BookingWidgetSmartFlow : BookingWidgetClassic;

  return (
    <div className="w-full">
      <Widget
        event={eventData.event}
        fields={eventData.fields}
        ruleSets={eventData.ruleSets}
        profile={eventData.profile}
        userEmail={eventData.profile.email}
        metaPixelIds={eventData.metaPixelIds}
        teamId={eventData.teamId}
        metaPixelEventNames={eventData.metaPixelEventNames}
        confirmationIncludeCancelReschedule={eventData.confirmationIncludeCancelReschedule}
        gtmContainerId={eventData.gtmContainerId}
        eventSlug={eventData.event.slug}
        bookingPaused={eventData.bookingPaused}
        fillContainer
      />
    </div>
  );
}
